import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  content?: string | null;
};

export default function UserImportantAlertBanner({ content }: Props) {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [isHighlighted, setIsHighlighted] = useState(false);

  useEffect(() => {
    if (!content) {
      setIsVisible(false);
      setIsHighlighted(false);
      return undefined;
    }

    setIsVisible(false);
    setIsHighlighted(true);

    const animationFrameId = requestAnimationFrame(() => {
      setIsVisible(true);
    });
    const highlightTimeoutId = window.setTimeout(() => {
      setIsHighlighted(false);
    }, 1200);

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.clearTimeout(highlightTimeoutId);
    };
  }, [content]);

  if (!content) return null;

  const highlightStyle = isHighlighted
    ? {
        boxShadow:
          '0 0 0 3px rgba(251, 146, 60, 0.35), 0 8px 18px rgba(249, 115, 22, 0.16)',
      }
    : undefined;

  return (
    <div
      style={highlightStyle}
      className={`mx-5 mb-4 rounded-md border border-orange-300 border-l-4 border-l-orange-500 bg-orange-50 px-4 py-3 text-orange-950 shadow-sm motion-safe:transition-all motion-safe:duration-500 motion-safe:ease-out ${
        isVisible ? 'translate-y-0 opacity-100' : '-translate-y-1 opacity-0'
      }`}
    >
      <div className="flex gap-3">
        <div
          aria-hidden="true"
          className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-orange-500 text-xs font-bold leading-none text-white"
        >
          !
        </div>
        <div>
          <div className="mb-1 text-sm font-bold uppercase tracking-wide">
            {t('front.userImportantAlert.title')}
          </div>
          <div className="whitespace-pre-line text-sm font-semibold leading-5">
            {content}
          </div>
        </div>
      </div>
    </div>
  );
}
